import type { Company } from '../types/company';
import { resolveRenderSurfaceCompanyId } from './resolveWorkspaceCompanyId';
import {
  resolveKryspaceWorkspaceSlug,
  type ResolvedKryspaceWorkspaceSlug,
} from './resolveKryspaceWorkspaceSlug';

export type RenderSurfaceCompanyRow = {
  company: Pick<Company, 'id' | 'name' | 'kryspace_slug' | 'metadata'>;
};

export interface RenderSurfaceSlugSessionInput {
  routeSlug?: string;
  selectedCompany?: { id?: string; name?: string; kryspace_slug?: string } | null;
  availableCompanies: RenderSurfaceCompanyRow[];
  userCompanyId?: string;
  persistedCompanyId?: string | null;
  persistedKryspaceSlug?: string | null;
}

export interface RenderSurfaceSlugSession {
  workspaceCompanyId?: string;
  companyRecord?: RenderSurfaceCompanyRow['company'];
  derivedSlug?: ResolvedKryspaceWorkspaceSlug;
  slugFromRoute: boolean;
  effectiveSlug: string;
  requiresCompanyContext: boolean;
}

/** Header company + optional persisted slug drive render / Control Bridge kryspace session. */
export function resolveRenderSurfaceSlugSession(
  input: RenderSurfaceSlugSessionInput
): RenderSurfaceSlugSession {
  const headerCompanyId = input.selectedCompany?.id ?? input.persistedCompanyId ?? undefined;
  const workspaceCompanyId = resolveRenderSurfaceCompanyId(
    input.userCompanyId,
    headerCompanyId,
    input.availableCompanies
  );

  const companyRecord = workspaceCompanyId
    ? input.availableCompanies.find((row) => row.company.id === workspaceCompanyId)?.company
    : undefined;

  const derivedSlug = companyRecord ? resolveKryspaceWorkspaceSlug(companyRecord) : undefined;
  const sessionSlug =
    input.selectedCompany?.kryspace_slug?.trim() ||
    input.persistedKryspaceSlug?.trim() ||
    undefined;

  const slugFromRoute = Boolean(input.routeSlug?.trim());
  const effectiveSlug = (input.routeSlug?.trim() || sessionSlug || derivedSlug?.slug || '').trim();

  return {
    workspaceCompanyId,
    companyRecord,
    derivedSlug,
    slugFromRoute,
    effectiveSlug,
    requiresCompanyContext: !slugFromRoute,
  };
}

/**
 * Workspace slug that is safe to load for Control Bridge (kryspace read API).
 * Name-hint guesses (from company display name) are not real workspaces yet —
 * using them produces "global_slug_registry unavailable" / UNKNOWN_SLUG errors.
 */
export function controlBridgeWorkspaceSlug(
  session: RenderSurfaceSlugSession,
  selectedCompanyKryspaceSlug?: string | null
): string {
  if (session.slugFromRoute) {
    return session.effectiveSlug.trim();
  }
  const fromSelected = selectedCompanyKryspaceSlug?.trim();
  if (fromSelected) {
    return fromSelected;
  }
  const fromRecord = session.companyRecord?.kryspace_slug?.trim();
  if (fromRecord) {
    return fromRecord;
  }
  if (
    session.derivedSlug?.source === 'metadata' &&
    session.derivedSlug.slug?.trim()
  ) {
    return session.derivedSlug.slug.trim();
  }
  return '';
}
